import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { QRCodeSVG } from "qrcode.react";
import {
  MessageCircle, Send, Save, RefreshCw, Loader2, Info,
  LogIn, LogOut, AlarmClock, Hourglass, CalendarX, UserPlus,
  Wifi, WifiOff, Link2, Smartphone,
} from "lucide-react";

const LINE_GREEN = "#06C755";

const EVENT_TOGGLES = [
  { key: "notify_clock_in", label: "出勤打刻", desc: "出勤を記録したとき", icon: LogIn },
  { key: "notify_clock_out", label: "退勤打刻", desc: "退勤を記録したとき（出勤/退勤時刻つき）", icon: LogOut },
  { key: "notify_late", label: "遅刻", desc: "始業時刻＋猶予を過ぎて出勤したとき", icon: AlarmClock },
  { key: "notify_overtime", label: "残業", desc: "退勤時に残業が発生したとき", icon: Hourglass },
  { key: "notify_missing_out", label: "退勤打刻漏れ", desc: "毎日深夜に前日分を自動チェック", icon: CalendarX },
];

const TYPE_LABEL = { clock_in: "出勤", clock_out: "退勤", late: "遅刻", overtime: "残業", missing_out: "打刻漏れ", register: "登録", test: "テスト" };
const RECIPIENT_LABEL = { employee: "本人", admin: "運営", test: "テスト" };
const STATUS_STYLE = { sent: "bg-emerald-600 text-white", mocked: "bg-amber-500 text-white", failed: "bg-red-600 text-white" };

export default function LineSettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [adminIdsText, setAdminIdsText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [testUserId, setTestUserId] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [testing, setTesting] = useState(false);
  const [companyCode, setCompanyCode] = useState("");
  const [regStatus, setRegStatus] = useState(null);
  const [receiver, setReceiver] = useState(null);
  const [recvQr, setRecvQr] = useState(null);
  const [recvPin, setRecvPin] = useState(null);
  const [recvConnecting, setRecvConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const recvPollRef = useRef(null);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/line/settings`);
      setSettings(res.data);
      setAdminIdsText((res.data.admin_line_ids || []).join("\n"));
    } catch (e) {
      toast.error("設定の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/line/logs?limit=50`);
      setLogs(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [meRes, statusRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/line/registration-status`),
      ]);
      setCompanyCode(meRes.data?.company?.code || "");
      setRegStatus(statusRes.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadReceiver = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/line/receiver/status`);
      setReceiver(res.data);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const pollReceiver = useCallback(() => {
    if (recvPollRef.current) clearInterval(recvPollRef.current);
    recvPollRef.current = setInterval(async () => {
      try {
        const st = await loadReceiver();
        if (st && st.service === "connected") {
          setRecvQr(null); setRecvPin(null); setRecvConnecting(false);
          clearInterval(recvPollRef.current);
          toast.success("LINEが接続されました");
        } else {
          try {
            const q = await axios.get(`${API}/line/receiver/qr`);
            if (q.data?.qr) setRecvQr(q.data.qr);
            if (q.data?.pin) setRecvPin(q.data.pin);
          } catch (e) { /* noop */ }
        }
      } catch (e) { /* noop */ }
    }, 2500);
  }, [loadReceiver]);

  useEffect(() => {
    loadSettings();
    loadLogs();
    loadMeta();
    loadReceiver();
    return () => { if (recvPollRef.current) clearInterval(recvPollRef.current); };
  }, [loadSettings, loadLogs, loadMeta, loadReceiver]);

  const connectReceiver = async () => {
    setRecvConnecting(true);
    try {
      await axios.post(`${API}/line/receiver/connect`, {});
      toast.message("LINEに接続中… QRコードをLINEアプリで読み取ってください");
      setTimeout(() => { loadReceiver(); pollReceiver(); }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "受信サービスに接続できません");
      setRecvConnecting(false);
    }
  };

  const disconnectReceiver = async () => {
    setDisconnecting(true);
    try {
      await axios.post(`${API}/line/receiver/disconnect`, {});
      if (recvPollRef.current) clearInterval(recvPollRef.current);
      setRecvQr(null); setRecvPin(null); setRecvConnecting(false);
      toast.success("LINEからログアウトしました。「接続する」で再ログインできます。");
      setTimeout(loadReceiver, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "ログアウトに失敗しました");
    } finally {
      setDisconnecting(false);
    }
  };

  const setField = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const adminIds = adminIdsText.split(/[\n,、]+/).map((n) => n.trim()).filter(Boolean);
      const payload = {
        line_enabled: settings.line_enabled,
        admin_line_ids: adminIds,
        notify_employee: settings.notify_employee,
        notify_admin: settings.notify_admin,
        notify_clock_in: settings.notify_clock_in,
        notify_clock_out: settings.notify_clock_out,
        notify_late: settings.notify_late,
        notify_overtime: settings.notify_overtime,
        notify_missing_out: settings.notify_missing_out,
        work_start_time: settings.work_start_time,
        late_grace_minutes: settings.late_grace_minutes,
      };
      const res = await axios.put(`${API}/line/settings`, payload);
      setSettings(res.data);
      setAdminIdsText((res.data.admin_line_ids || []).join("\n"));
      toast.success("設定を保存しました");
    } catch (e) {
      toast.error("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testUserId.trim()) { toast.error("送信先のLINE userId を入力してください"); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/line/test`, { userId: testUserId.trim(), message: testMessage.trim() || undefined });
      if (res.data.status === "sent") toast.success("テスト送信しました");
      else if (res.data.status === "mocked") toast.message("モック送信（LINE未接続）");
      else toast.error("送信に失敗しました: " + (res.data.error || ""));
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "送信に失敗しました");
    } finally {
      setTesting(false);
    }
  };

  const registerUrl = companyCode ? `${window.location.origin}/line-register?company=${companyCode}` : "";

  if (loading || !settings) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  const svc = receiver?.service;
  const connected = svc === "connected";

  return (
    <div className="space-y-6 pb-16" data-testid="line-settings-page">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: LINE_GREEN }}>
          <MessageCircle className="w-5 h-5 text-white" />
        </span>
        <div>
          <h1 className="text-xl font-bold text-white">LINE通知</h1>
          <p className="text-sm text-slate-400">打刻・勤怠のお知らせをLINEへ（WhatsAppと併用）</p>
        </div>
      </div>

      {/* 配信者（送信元）と通知内容の説明 */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="line-info-card">
        <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Info className="w-4 h-4" style={{ color: LINE_GREEN }} />配信者と通知内容</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-300 space-y-2">
          <p><b className="text-white">配信者（送信元）</b>：接続中のLINEアカウント{receiver?.display_name ? `（${receiver.display_name}）` : ""}。通知本文の先頭には会社名が付きます。</p>
          <p><b className="text-white">通知内容</b>：出勤打刻／退勤打刻（出勤・退勤時刻つき）／遅刻／残業／退勤打刻漏れ／登録完了。本人と運営（管理者）に届きます。</p>
        </CardContent>
      </Card>

      {/* 接続カード */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="line-connect-card">
        <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Smartphone className="w-4 h-4" style={{ color: LINE_GREEN }} />送受信用LINEの接続</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {connected ? <Wifi className="w-5 h-5 text-emerald-400" /> : <WifiOff className="w-5 h-5 text-slate-500" />}
            <div className="flex-1">
              <p className="text-sm text-white font-medium">
                {connected ? `接続中${receiver?.display_name ? `：${receiver.display_name}` : ""}` : svc === "qr_ready" || svc === "connecting" ? "接続待ち（QR読み取り）" : "未接続"}
              </p>
              <p className="text-xs text-slate-400">{connected ? "通知を実送信できます" : "「接続する」を押してLINEアプリでQRを読み取ってください"}</p>
            </div>
            {connected ? (
              <Button onClick={disconnectReceiver} disabled={disconnecting} size="sm" variant="outline" className="border-red-500/60 text-red-300 hover:bg-red-500/10" data-testid="line-logout-btn">
                {disconnecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LogOut className="w-4 h-4 mr-1" />}
                ログアウト
              </Button>
            ) : (
              <Button onClick={connectReceiver} disabled={recvConnecting} size="sm" style={{ background: LINE_GREEN }} data-testid="line-connect-btn">
                {recvConnecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                接続する
              </Button>
            )}
          </div>
          {recvQr && !connected && (
            <div className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl" data-testid="line-recv-qr">
              <img src={recvQr} alt="LINE QR" className="w-48 h-48" />
              <p className="text-xs text-slate-700">LINEアプリ → ホーム → 友だち追加 → QRコード で読み取り</p>
              {recvPin && <p className="text-sm font-bold text-slate-900">スマホに表示されたら PIN: <span className="font-mono">{recvPin}</span> を入力</p>}
            </div>
          )}
          {!connected && (svc === "qr_ready" || svc === "connecting") && (
            <button type="button" onClick={disconnectReceiver} disabled={disconnecting} className="text-xs text-slate-400 hover:text-slate-200 w-full text-center" data-testid="line-reset-btn">
              うまくいかない場合は「最初からやり直す（ログアウト）」
            </button>
          )}
        </CardContent>
      </Card>

      {/* 有効化 + 運営ID */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="line-main-settings">
        <CardHeader><CardTitle className="text-white text-base">基本設定</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div><p className="text-white font-medium">LINE通知を有効にする</p><p className="text-xs text-slate-400">オフにするとLINEへは送信しません（WhatsAppは別設定）</p></div>
            <Switch checked={!!settings.line_enabled} onCheckedChange={(v) => setField("line_enabled", v)} data-testid="line-enabled-switch" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between rounded-lg bg-slate-900/40 p-3">
              <span className="text-sm text-slate-200">本人に送る</span>
              <Switch checked={!!settings.notify_employee} onCheckedChange={(v) => setField("notify_employee", v)} data-testid="line-notify-employee" />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-900/40 p-3">
              <span className="text-sm text-slate-200">運営に送る</span>
              <Switch checked={!!settings.notify_admin} onCheckedChange={(v) => setField("notify_admin", v)} data-testid="line-notify-admin" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200">運営（管理者）のLINE userId</Label>
            <Textarea value={adminIdsText} onChange={(e) => setAdminIdsText(e.target.value)} placeholder="1行に1つ（例: U1234567890abcdef...）。本人がこの画面下の登録リンクから登録すると取得できます。" className="bg-slate-900 border-slate-600 text-white min-h-[80px] font-mono text-xs" data-testid="line-admin-ids" />
          </div>
        </CardContent>
      </Card>

      {/* 通知イベント（WhatsAppと共通） */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="line-event-toggles">
        <CardHeader><CardTitle className="text-white text-base">通知するイベント<span className="text-xs font-normal text-slate-400 ml-2">（WhatsApp通知と共通）</span></CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {EVENT_TOGGLES.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.key} className="flex items-center justify-between rounded-lg bg-slate-900/40 p-3">
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-slate-400" />
                  <div><p className="text-sm text-slate-100">{t.label}</p><p className="text-xs text-slate-500">{t.desc}</p></div>
                </div>
                <Switch checked={!!settings[t.key]} onCheckedChange={(v) => setField(t.key, v)} data-testid={`line-toggle-${t.key}`} />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full h-11" style={{ background: LINE_GREEN }} data-testid="line-save-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}設定を保存
      </Button>

      {/* スタッフ登録 */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="line-register-card">
        <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><UserPlus className="w-4 h-4" style={{ color: LINE_GREEN }} />スタッフのLINE登録</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {regStatus && (
            <p className="text-sm text-slate-300">登録済み <b className="text-white">{regStatus.registered}</b> / {regStatus.total} 名</p>
          )}
          {receiver?.userid ? (
            <div className="rounded-xl bg-slate-900/40 p-3 flex items-center gap-4" data-testid="line-addfriend-block">
              <div className="bg-white p-2 rounded-lg"><QRCodeSVG value={`https://line.me/ti/p/~${receiver.userid}`} size={92} /></div>
              <div className="text-xs text-slate-300 space-y-1">
                <p className="text-slate-100 font-medium">友だち追加用QR</p>
                <p className="text-slate-400">スタッフはこのQRで会社LINEを友だち追加できます</p>
                <a href={`https://line.me/ti/p/~${receiver.userid}`} target="_blank" rel="noreferrer" className="break-all font-mono" style={{ color: LINE_GREEN }}>line.me/ti/p/~{receiver.userid}</a>
              </div>
            </div>
          ) : connected ? (
            <p className="text-xs text-amber-300" data-testid="line-noid-note">友だち追加リンクを出すには、会社LINEに「LINE ID」を設定してください（LINEアプリ → ホーム → 設定 → プロフィール → ID）。</p>
          ) : null}
          {registerUrl && (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-white p-3 rounded-xl"><QRCodeSVG value={registerUrl} size={120} /></div>
              <div className="text-sm text-slate-300 space-y-1">
                <p>スタッフにこのQR（または下のURL）を渡してください。</p>
                <p className="break-all text-xs text-slate-400 font-mono">{registerUrl}</p>
                <p className="text-xs text-slate-500">スタッフ: QRで本人確認 → 「友だち追加」→ 「コードを送信」をタップするだけ（入力不要・2タップ）</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* テスト送信 */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="line-test-card">
        <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Send className="w-4 h-4" style={{ color: LINE_GREEN }} />テスト送信</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input value={testUserId} onChange={(e) => setTestUserId(e.target.value)} placeholder="送信先 LINE userId（U...）" className="bg-slate-900 border-slate-600 text-white font-mono text-sm" data-testid="line-test-userid" />
          <Input value={testMessage} onChange={(e) => setTestMessage(e.target.value)} placeholder="メッセージ（空欄で既定文）" className="bg-slate-900 border-slate-600 text-white" data-testid="line-test-message" />
          <Button onClick={handleTest} disabled={testing} variant="outline" className="w-full" style={{ borderColor: LINE_GREEN, color: LINE_GREEN }} data-testid="line-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}テスト送信
          </Button>
        </CardContent>
      </Card>

      {/* ログ */}
      <Card className="bg-slate-800/60 border-slate-700" data-testid="line-logs-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white text-base">送信履歴</CardTitle>
          <Button onClick={loadLogs} size="sm" variant="ghost" className="text-slate-400" data-testid="line-logs-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">まだ送信履歴はありません</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {logs.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-xs border-b border-slate-700/50 pb-2" data-testid="line-log-row">
                  <Badge className={STATUS_STYLE[l.status] || "bg-slate-600"}>{l.status === "sent" ? "送信" : l.status === "mocked" ? "モック" : "失敗"}</Badge>
                  <span className="text-slate-300">{TYPE_LABEL[l.type] || l.type}</span>
                  <span className="text-slate-500">{RECIPIENT_LABEL[l.recipient_type] || l.recipient_type}</span>
                  <span className="text-slate-400 font-mono truncate flex-1">{l.employee_name || l.to}</span>
                  <span className="text-slate-600">{(l.created_at || "").slice(5, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
